import JSZip from 'jszip';
import arbeitszeitTemplateUrl from '../../templates/Arbeitszeit - template.odt?url';
import arbeitszeitOfficialUrl from '../../templates/Arbeitszeit.odt?url';
import { supabase } from '../lib/supabase';

function escapeXml(value) {
  if (value === null || value === undefined) return '';
  return String(value)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&apos;');
}

function formatDate(value) {
  if (!value) return '';
  try {
    const d = new Date(value);
    if (Number.isNaN(d.getTime())) return String(value);
    return d.toLocaleDateString('de-DE', { timeZone: 'Europe/Berlin' });
  } catch {
    return String(value || '');
  }
}

function escapeRegExp(string) {
  return string.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

function buildPlaceholderMap(employee) {
  const numberOrEmpty = (v) => (v === null || v === undefined ? '' : String(v));

  return {
    '{{NAME}}': employee?.name || '',
    '{{EMPLOYEE_CODE}}': employee?.employeeCode || '',
    '{{EMAIL}}': employee?.email || '',
    '{{PHONE}}': employee?.phone || '',
    '{{POSITION}}': employee?.position || '',
    '{{HIRE_DATE}}': formatDate(employee?.hireDate),
    '{{HIRE_TYPE}}': employee?.hireType || '',
    '{{GEHALT}}': numberOrEmpty(employee?.salary),
    '{{GEHALT_S}}': numberOrEmpty(employee?.gehaltS),
    '{{TAGESRATE}}': numberOrEmpty(employee?.tagesrate),
    '{{TOTAL_GEHALT_S}}': numberOrEmpty(employee?.totalGehaltS),
    '{{CREATED_AT}}': formatDate(employee?.createdAt),
    '{{UPDATED_AT}}': formatDate(employee?.updatedAt),
  };
}

export async function generateArbeitszeitOdtBlob(employee) {
  const response = await fetch(arbeitszeitTemplateUrl);
  if (!response.ok) {
    throw new Error('Vorlage konnte nicht geladen werden');
  }
  const templateArrayBuffer = await response.arrayBuffer();

  const zip = await JSZip.loadAsync(templateArrayBuffer);

  const contentFile = zip.file('content.xml');
  if (!contentFile) {
    throw new Error('Ungültige ODT-Vorlage: content.xml fehlt');
  }

  let contentXml = await contentFile.async('string');

  // 1) Replace simple placeholders (header area etc.)
  const placeholders = buildPlaceholderMap(employee);
  Object.entries(placeholders).forEach(([token, value]) => {
    const xmlSafe = escapeXml(value);
    const re = new RegExp(escapeRegExp(token), 'g');
    contentXml = contentXml.replace(re, xmlSafe);
  });

  // 2) Fill Arbeitszeit table rows for current month
  try {
    const now = new Date();
    const berlinTz = 'Europe/Berlin';
    // Compute monthStart and monthEnd in Berlin local time
    const berlinDateParts = new Intl.DateTimeFormat('en-CA', { timeZone: berlinTz, year: 'numeric', month: '2-digit', day: '2-digit' }).formatToParts(now);
    const y = Number(berlinDateParts.find(p => p.type === 'year')?.value);
    const m = Number(berlinDateParts.find(p => p.type === 'month')?.value) - 1; // 0-11
    const monthStart = new Date(Date.UTC(y, m, 1, 0, 0, 0));
    const monthEnd = new Date(Date.UTC(y, m + 1, 0, 23, 59, 59));

    const entries = await fetchEmployeeTimeEntriesMonthly(employee?.id, monthStart, monthEnd);
    const dailyRows = buildDailyRows(entries, monthStart, monthEnd);
    contentXml = fillArbeitszeitTable(contentXml, dailyRows);
    // Calculate total duration (HH:MM) and update "Summe" field
    const totalMinutes = dailyRows.reduce((sum, r) => {
      if (!r?.duration || r.duration === '-') return sum;
      const m = String(r.duration).match(/^(\d{1,2}):(\d{2})$/);
      if (!m) return sum;
      return sum + parseInt(m[1], 10) * 60 + parseInt(m[2], 10);
    }, 0);
    const totalHH = String(Math.floor(totalMinutes / 60)).padStart(2, '0');
    const totalMM = String(totalMinutes % 60).padStart(2, '0');
    const totalText = `${totalHH}:${totalMM}`;
    contentXml = updateSumParagraph(contentXml, totalText);
  } catch (tableErr) {
    // If table filling fails, keep header-only replacement
    console.warn('Konnte Tabelle nicht automatisch füllen:', tableErr);
  }

  zip.file('content.xml', contentXml);

  const blob = await zip.generateAsync({
    type: 'blob',
    mimeType: 'application/vnd.oasis.opendocument.text',
  });

  return blob;
}

export async function downloadArbeitszeitOdt(employee) {
  const blob = await generateArbeitszeitOdtBlob(employee);
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  const safeName = (employee?.name || 'mitarbeiter')
    .replace(/[^a-zA-Z0-9-_\.]/g, '_')
    .slice(0, 60);
  a.href = url;
  a.download = `Arbeitszeit_${safeName}.odt`;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

export async function downloadArbeitszeitOfficial(employee) {
  const response = await fetch(arbeitszeitOfficialUrl);
  if (!response.ok) {
    throw new Error('Vorlage Arbeitszeit.odt konnte nicht geladen werden');
  }
  const blob = await response.blob();
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  const safeName = (employee?.name || 'mitarbeiter')
    .replace(/[^a-zA-Z0-9-_\.]/g, '_')
    .slice(0, 60);
  a.href = url;
  a.download = `Arbeitszeit_offiziell_${safeName}.odt`;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

// --- Internal helpers for monthly table filling ---

async function fetchEmployeeTimeEntriesMonthly(employeeId, start, end) {
  if (!employeeId) return [];
  // Query a slightly wider UTC window to safely include Europe/Berlin local days
  const utcStart = new Date(Date.UTC(start.getFullYear(), start.getMonth(), 1, 0, 0, 0));
  const utcEndExclusive = new Date(Date.UTC(end.getFullYear(), end.getMonth(), end.getDate() + 1, 0, 0, 0));
  const wideStart = new Date(utcStart.getTime() - 24 * 60 * 60 * 1000);
  const wideEnd = new Date(utcEndExclusive.getTime() + 24 * 60 * 60 * 1000);

  const { data, error } = await supabase
    .from('time_entries')
    .select('id, clock_in_time, clock_out_time, total_hours, break_duration, notes, created_at')
    .eq('employee_id', employeeId)
    .gte('clock_in_time', wideStart.toISOString())
    .lt('clock_in_time', wideEnd.toISOString());
  if (error) throw error;
  return data || [];
}

function buildDailyRows(entries, start, end) {
  const daysInMonth = new Date(start.getFullYear(), start.getMonth() + 1, 0).getDate();
  const byDay = {};
  const berlinTZ = 'Europe/Berlin';
  const dayFmt = new Intl.DateTimeFormat('de-DE', { timeZone: berlinTZ, day: '2-digit' });
  const dateFmt = new Intl.DateTimeFormat('de-DE', { timeZone: berlinTZ, year: 'numeric', month: '2-digit', day: '2-digit' });
  const timeFmt = new Intl.DateTimeFormat('de-DE', { timeZone: berlinTZ, hour: '2-digit', minute: '2-digit', hour12: false });
  entries.forEach((e) => {
    const inDate = e.clock_in_time ? new Date(e.clock_in_time) : null;
    if (!inDate) return;
    const day = Number(dayFmt.format(inDate));
    if (!byDay[day]) {
      byDay[day] = [];
    }
    byDay[day].push(e);
  });

  const rows = [];
  for (let d = 1; d <= daysInMonth; d += 1) {
    const list = byDay[d] || [];
    if (list.length === 0) {
      rows.push({
        day: String(d),
        start: '-',
        break: '-',
        end: '-',
        duration: '-',
        recordedAt: '-',
        notes: '-',
      });
      continue;
    }

    let minStart = null;
    let minStartRaw = null;
    let maxEnd = null;
    let maxEndRaw = null;
    let totalHours = 0;
    let totalBreak = 0;
    let recordedAt = null;
    const notes = [];

    list.forEach((e) => {
      const inDate = e.clock_in_time ? new Date(e.clock_in_time) : null;
      const outDate = e.clock_out_time ? new Date(e.clock_out_time) : null;
      if (inDate && (!minStart || inDate < minStart)) { minStart = inDate; minStartRaw = e.clock_in_time; }
      if (outDate && (!maxEnd || outDate > maxEnd)) { maxEnd = outDate; maxEndRaw = e.clock_out_time; }
      totalHours += Number(e.total_hours || 0);
      totalBreak += Number(e.break_duration || 0);
      const rec = e.created_at ? new Date(e.created_at) : null;
      if (rec && (!recordedAt || rec > recordedAt)) recordedAt = rec;
      if (e.notes) notes.push(e.notes);
    });

    // Force 15-minute break on any day with entries
    const forcedBreakMinutes = list.length > 0 ? 15 : 0;

    const formatTime = (date, raw) => {
      if (!date) return '-';
      // Prefer Berlin formatting, but guard against double-offset issues by
      // comparing with raw string hour:minute when available.
      const berlin = timeFmt.format(date);
      if (typeof raw === 'string') {
        const m = raw.match(/T(\d{2}:\d{2})/);
        if (m && m[1]) {
          const rawHM = m[1];
          // if the difference between parsed berlin time and rawHM is >= 120 minutes,
          // assume double shift and use rawHM
          const [bh, bm] = berlin.split(':').map(n => parseInt(n, 10));
          const [rh, rm] = rawHM.split(':').map(n => parseInt(n, 10));
          if (!Number.isNaN(bh) && !Number.isNaN(rh)) {
            const diffMinutes = ((bh * 60 + bm) - (rh * 60 + rm) + 1440) % 1440;
            if (diffMinutes === 120 || diffMinutes === 180) {
              return rawHM;
            }
          }
        }
      }
      return berlin;
    };
    const formatBreak = (value) => {
      if (!value) return '-';
      return '15 Min';
    };

    const formatHoursMinutes = (hoursFloat) => {
      const totalMinutes = Math.max(0, Math.round((hoursFloat || 0) * 60));
      const hh = Math.floor(totalMinutes / 60);
      const mm = totalMinutes % 60;
      return `${String(hh).padStart(2, '0')}:${String(mm).padStart(2, '0')}`;
    };

    // Calculate duration as span between first in and last out minus fixed break
    let durationHours = 0;
    if (minStart && maxEnd) {
      const spanHours = (maxEnd.getTime() - minStart.getTime()) / (1000 * 60 * 60);
      durationHours = Math.max(0, spanHours - forcedBreakMinutes / 60);
    } else if (totalHours > 0) {
      durationHours = Number(totalHours);
    }

    rows.push({
      day: String(d),
      start: formatTime(minStart, minStartRaw),
      break: formatBreak(forcedBreakMinutes),
      end: formatTime(maxEnd, maxEndRaw),
      duration: durationHours > 0 ? formatHoursMinutes(durationHours) : '-',
      recordedAt: recordedAt ? dateFmt.format(recordedAt) : '-',
      notes: notes.length ? Array.from(new Set(notes)).join('; ') : '-',
    });
  }

  return rows;
}

function fillArbeitszeitTable(contentXml, rows) {
  const parser = new DOMParser();
  const xmlDoc = parser.parseFromString(contentXml, 'text/xml');

  const TABLE_NS = 'urn:oasis:names:tc:opendocument:xmlns:table:1.0';
  const TEXT_NS = 'urn:oasis:names:tc:opendocument:xmlns:text:1.0';

  const tables = xmlDoc.getElementsByTagName('table:table');
  let targetTable = null;
  for (let t = 0; t < tables.length; t += 1) {
    const tbl = tables[t];
    const firstRow = tbl.getElementsByTagName('table:table-row')[0];
    if (!firstRow) continue;
    const headerText = firstRow.textContent || '';
    if (
      headerText.includes('Kalendertag') &&
      headerText.includes('Beginn') &&
      headerText.includes('Pause') &&
      headerText.includes('Ende') &&
      headerText.includes('Dauer') &&
      headerText.includes('Bemerkungen')
    ) {
      targetTable = tbl;
      break;
    }
  }

  if (!targetTable) {
    throw new Error('Arbeitszeit-Tabelle nicht gefunden');
  }

  const tableRows = targetTable.getElementsByTagName('table:table-row');
  // Assume row 0 is header; data starts at row 1
  for (let i = 1; i < tableRows.length && i <= rows.length; i += 1) {
    const r = rows[i - 1];
    const rowEl = tableRows[i];
    const cells = rowEl.getElementsByTagName('table:table-cell');
    const values = [r.day, r.start, r.break, r.end, r.duration, r.recordedAt, r.notes];
    for (let c = 0; c < cells.length && c < values.length; c += 1) {
      const cell = cells[c];
      // Preserve existing paragraph and its style (alignment) when present
      const existingPs = cell.getElementsByTagName('text:p');
      if (existingPs && existingPs.length > 0) {
        const p = existingPs[0];
        // Remove children of the first paragraph only, keep attributes/styles
        while (p.firstChild) p.removeChild(p.firstChild);
        p.appendChild(xmlDoc.createTextNode(values[c] == null ? '' : String(values[c])));
        // Remove any extra paragraphs to avoid stacked content
        for (let k = existingPs.length - 1; k >= 1; k -= 1) {
          const extra = existingPs[k];
          extra.parentNode && extra.parentNode.removeChild(extra);
        }
      } else {
        const p = xmlDoc.createElementNS(TEXT_NS, 'text:p');
        p.textContent = values[c] == null ? '' : String(values[c]);
        cell.appendChild(p);
      }
    }
  }

  const serializer = new XMLSerializer();
  return serializer.serializeToString(xmlDoc);
}

function updateSumParagraph(contentXml, totalText) {
  try {
    const parser = new DOMParser();
    const xmlDoc = parser.parseFromString(contentXml, 'text/xml');
    const TEXT_NS = 'urn:oasis:names:tc:opendocument:xmlns:text:1.0';
    const paragraphs = xmlDoc.getElementsByTagName('text:p');
    for (let i = paragraphs.length - 1; i >= 0; i -= 1) {
      const p = paragraphs[i];
      const text = (p.textContent || '').trim();
      if (/summe/i.test(text) || /dumme/i.test(text)) {
        // Keep style: add a centered span with the label+value
        while (p.firstChild) p.removeChild(p.firstChild);
        const span = xmlDoc.createElementNS(TEXT_NS, 'text:span');
        // Reuse existing style if present on paragraph
        const styleName = p.getAttribute('text:style-name');
        if (styleName) span.setAttribute('text:style-name', styleName);
        span.appendChild(xmlDoc.createTextNode(`Summe: ${totalText}`));
        p.appendChild(span);
        break;
      }
    }
    const serializer = new XMLSerializer();
    return serializer.serializeToString(xmlDoc);
  } catch (e) {
    console.warn('Konnte Summe nicht setzen:', e);
    return contentXml;
  }
}


